import { useState, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { backendAPI } from '../api/backend'
import { handleImageError } from '../utils/imageUtils'
import './TextSearch.css'

/**
 * TextSearch Component
 *
 * Free text search for affiliate products with "Load More" pagination.
 * Displays 50 results at a time with the ability to load more infinitely.
 */
const TextSearch = () => {
  const location = useLocation()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalAvailable, setTotalAvailable] = useState(0)
  const [dataSource, setDataSource] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [initialQueryProcessed, setInitialQueryProcessed] = useState(false)

  const RESULTS_PER_PAGE = 50

  // Handle initial query from navigation state
  useEffect(() => {
    if (location.state?.initialQuery && !initialQueryProcessed) {
      setQuery(location.state.initialQuery)
      setInitialQueryProcessed(true)
    }
  }, [location.state, initialQueryProcessed])

  // Auto-search when query is set from initial state
  useEffect(() => {
    if (initialQueryProcessed && query && !hasSearched) {
      performSearch()
    }
  }, [initialQueryProcessed, query, hasSearched])

  const performSearch = useCallback(async () => {
    if (!query.trim()) {
      setError('Please enter a search term')
      return
    }

    setIsLoading(true)
    setError(null)
    setResults([])
    setCurrentPage(1)
    setHasSearched(true)

    try {
      const response = await backendAPI.textSearch(query.trim(), 1, RESULTS_PER_PAGE)

      if (response.success) {
        setResults(response.results || [])
        setHasMore(response.has_more || false)
        setTotalAvailable(response.total_available || 0)
        setDataSource(response.data_source || 'unknown')
        setCurrentPage(1)
      } else {
        setError(response.error || 'Search failed')
      }
    } catch (err) {
      console.error('Search error:', err)
      setError('Failed to search. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [query])

  const handleSearch = useCallback(async (e) => {
    if (e) e.preventDefault()
    performSearch()
  }, [performSearch])

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return

    setIsLoadingMore(true)
    const nextPage = currentPage + 1

    try {
      const response = await backendAPI.textSearch(query.trim(), nextPage, RESULTS_PER_PAGE)

      if (response.success && response.results) {
        setResults(prev => [...prev, ...response.results])
        setHasMore(response.has_more || false)
        setCurrentPage(nextPage)
        setTotalAvailable(response.total_available || totalAvailable)
      }
    } catch (err) {
      console.error('Load more error:', err)
      setError('Failed to load more results.')
    } finally {
      setIsLoadingMore(false)
    }
  }, [query, currentPage, hasMore, isLoadingMore, totalAvailable])

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e)
    }
  }

  const handleProductClick = (product) => {
    // Open product in new tab
    if (product.product_url) {
      window.open(product.product_url, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div className="text-search">
      <div className="text-search-header">
        <h2>Search Products</h2>
        <p className="text-search-subtitle">Search for any fashion item from affiliate stores</p>
      </div>

      <form onSubmit={handleSearch} className="text-search-form">
        <div className="search-input-wrapper">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Search for clothing, shoes, accessories..."
            className="text-search-input"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="text-search-button"
            disabled={isLoading || !query.trim()}
          >
            {isLoading ? (
              <span className="button-spinner"></span>
            ) : (
              'Search'
            )}
          </button>
        </div>
      </form>

      {error && (
        <div className="text-search-error">
          {error}
        </div>
      )}

      {hasSearched && !isLoading && results.length === 0 && !error && (
        <div className="text-search-no-results">
          <p>No products found for "{query}"</p>
          <p className="no-results-hint">Try different keywords or check your spelling</p>
        </div>
      )}

      {results.length > 0 && (
        <div className="text-search-results">
          <div className="results-header">
            <span className="results-count">
              Showing {results.length} of {totalAvailable} results
            </span>
            {dataSource === 'rakuten' && (
              <span className="live-badge">Live Results</span>
            )}
          </div>

          <div className="results-grid">
            {results.map((product, index) => (
              <div
                key={product.product_id || index}
                className="product-card"
                onClick={() => handleProductClick(product)}
              >
                <div className="product-image-wrapper">
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.product_name}
                      className="product-image"
                      loading="lazy"
                      data-original-src={product.image_url}
                      onError={(e) => handleImageError(e, product.image_url)}
                    />
                  ) : (
                    <div className="product-image-placeholder">
                      <span>👗</span>
                    </div>
                  )}
                </div>
                <div className="product-info">
                  <h3 className="product-name" title={product.product_name}>
                    {product.product_name?.length > 50
                      ? product.product_name.substring(0, 50) + '...'
                      : product.product_name}
                  </h3>
                  <p className="product-brand">{product.brand || 'Fashion Brand'}</p>
                  <p className="product-price">
                    {product.price ? `$${product.price}` : 'View Price'}
                  </p>
                </div>
                <div className="product-overlay">
                  <span className="view-button">View Product →</span>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="load-more-wrapper">
              <button
                className="load-more-button"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <span className="button-spinner"></span>
                    Loading...
                  </>
                ) : (
                  <>
                    Load More
                    <span className="load-more-count">
                      ({totalAvailable - results.length} more)
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default TextSearch


